import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Circle, Users, Clock, Activity, MapPin, Globe, MonitorSmartphone, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';

function fmtLastSeen(iso, isID) {
  if (!iso) return isID ? 'Belum pernah' : 'Never';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return isID ? 'Baru saja' : 'Just now';
  if (diffMin < 60) return `${diffMin}m ${isID ? 'lalu' : 'ago'}`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}j ${isID ? 'lalu' : 'ago'}`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}h ${isID ? 'lalu' : 'ago'}`;
  return d.toLocaleDateString(isID ? 'id-ID' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso, isID) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(isID ? 'id-ID' : 'en-US', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminPresenceTab() {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const isID = lang !== 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(5);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/activity/online`, {
        headers,
        params: { minutes, limit: 100 },
      });
      setData(res.data);
    } catch (err) {
      console.warn('presence fetch failed', err);
    }
    setLoading(false);
    setRefreshing(false);
  }, [API, headers, minutes]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const online = data?.online || [];
  const recent = data?.recent || [];

  const categories = useMemo(() => {
    const set = new Set();
    [...online, ...recent].forEach(r => { if (r.profile_category) set.add(r.profile_category); });
    return Array.from(set).sort();
  }, [online, recent]);

  const filterFn = (r) => !categoryFilter || r.profile_category === categoryFilter;
  const filteredOnline = online.filter(filterFn);
  const filteredRecent = recent.filter(filterFn);

  if (loading) {
    return <div className="glass-card rounded-2xl p-8 text-center t-text-muted text-sm">{isID ? 'Memuat…' : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-presence-tab">
      {/* Header + controls */}
      <div className="glass-card rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-5 h-5 text-[#D98A94]" />
              <h2 className="text-lg font-bold t-text">{isID ? 'Aktivitas Realtime' : 'Realtime Activity'}</h2>
            </div>
            <p className="text-xs t-text-muted">
              {isID
                ? 'Pantau member yang sedang online dan riwayat login terakhir. Auto-refresh 30 detik.'
                : 'Monitor members currently online and last-login history. Auto-refresh every 30s.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] font-semibold t-text-muted uppercase tracking-widest">
              {isID ? 'Jendela' : 'Window'}
            </label>
            <select
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              data-testid="presence-window-select"
              className="rounded-lg border t-border bg-white/60 backdrop-blur-sm px-2 py-1.5 text-xs t-text focus:outline-none focus:ring-2 focus:ring-[#D98A94]/40"
            >
              <option value={1}>1 {isID ? 'menit' : 'min'}</option>
              <option value={5}>5 {isID ? 'menit' : 'min'}</option>
              <option value={15}>15 {isID ? 'menit' : 'min'}</option>
              <option value={60}>1 {isID ? 'jam' : 'hr'}</option>
              <option value={240}>4 {isID ? 'jam' : 'hr'}</option>
            </select>
            <button
              onClick={load}
              disabled={refreshing}
              data-testid="presence-refresh-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#D98A94] hover:bg-[#D98A94]/90 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {isID ? 'Refresh' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <KpiCard label={isID ? 'Online Sekarang' : 'Online Now'} value={online.length} tone="green" />
          <KpiCard label={isID ? 'Log Terakhir' : 'Recent Log'} value={recent.length} tone="rose" />
          <KpiCard label={isID ? 'Jendela Waktu' : 'Time Window'} value={`${minutes}m`} tone="amber" />
          <KpiCard label={isID ? 'Kategori Aktif' : 'Active Categories'} value={categories.length} tone="slate" />
        </div>

        {categories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            <ChipBtn active={!categoryFilter} onClick={() => setCategoryFilter('')} label={isID ? 'Semua' : 'All'} />
            {categories.map(c => (
              <ChipBtn key={c} active={categoryFilter === c} onClick={() => setCategoryFilter(c)} label={c} />
            ))}
          </div>
        )}
      </div>

      {/* Online now */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b t-border bg-green-50/40 flex items-center gap-2">
          <Circle className="w-2.5 h-2.5 fill-green-500 text-green-500 animate-pulse" />
          <h3 className="text-sm font-bold t-text">
            {isID ? 'Online Sekarang' : 'Online Now'} · {filteredOnline.length}
          </h3>
        </div>
        {filteredOnline.length === 0 ? (
          <div className="p-8 text-center t-text-muted text-xs">
            {isID ? 'Tidak ada member yang aktif pada jendela waktu ini.' : 'No members active in this time window.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <PresenceTable rows={filteredOnline} isID={isID} online />
          </div>
        )}
      </div>

      {/* Recent log */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b t-border bg-[#D98A94]/5 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[#D98A94]" />
          <h3 className="text-sm font-bold t-text">
            {isID ? 'Log Login Terakhir' : 'Recent Login Log'} · {filteredRecent.length}
          </h3>
        </div>
        {filteredRecent.length === 0 ? (
          <div className="p-8 text-center t-text-muted text-xs">
            {isID ? 'Belum ada log aktivitas.' : 'No activity log yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <PresenceTable rows={filteredRecent} isID={isID} />
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = 'rose' }) {
  const toneMap = {
    green: 'from-green-50 to-white border-green-200 text-green-700',
    rose: 'from-[#FFF7F5] to-white border-[#D98A94]/25 text-[#C0727C]',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-700',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${toneMap[tone]} px-3 py-2.5`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function ChipBtn({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${active ? 'bg-[#D98A94] text-white border-[#D98A94]' : 'bg-white/60 t-text-secondary border-[#D98A94]/25 hover:bg-[#D98A94]/10'}`}
    >
      {label}
    </button>
  );
}

function PresenceTable({ rows, isID, online = false }) {
  return (
    <table className="w-full text-xs" data-testid={online ? 'presence-online-table' : 'presence-recent-table'}>
      <thead className="bg-white/40 border-b t-border">
        <tr className="text-left">
          <Th>{isID ? 'Nama Akun' : 'Account Name'}</Th>
          <Th>{isID ? 'Profil Klaim' : 'Claimed Profile'}</Th>
          <Th>{isID ? 'Kategori' : 'Category'}</Th>
          <Th>{isID ? 'Lokasi' : 'Location'}</Th>
          <Th>{isID ? 'Halaman' : 'Page'}</Th>
          <Th>{isID ? 'Terakhir' : 'Last Seen'}</Th>
        </tr>
      </thead>
      <tbody className="t-text">
        {rows.map(r => (
          <tr key={r.id} className="border-b t-border/40 hover:bg-white/30" data-testid={`presence-row-${r.id}`}>
            <Td>
              <div className="flex items-center gap-1.5">
                {online && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />}
                <div>
                  <div className="font-semibold truncate">{r.name || '—'}</div>
                  {r.whatsapp && <div className="text-[10px] t-text-muted">{r.whatsapp}</div>}
                </div>
              </div>
            </Td>
            <Td>
              <div className="font-medium">{r.profile_name || <span className="t-text-muted italic">{isID ? 'Belum klaim' : 'Not claimed'}</span>}</div>
            </Td>
            <Td>
              {r.profile_category ? (
                <span className="inline-block px-1.5 py-0.5 rounded-md bg-[#D98A94]/10 text-[#C0727C] text-[10px] font-semibold">
                  {r.profile_category}
                </span>
              ) : <span className="t-text-muted">—</span>}
            </Td>
            <Td>
              <div className="flex items-center gap-1 t-text-muted">
                {r.profile_city || r.profile_country ? <MapPin className="w-3 h-3" /> : null}
                <span className="truncate">{[r.profile_city, r.profile_country].filter(Boolean).join(', ') || '—'}</span>
              </div>
            </Td>
            <Td>
              <div className="flex items-center gap-1 t-text-muted max-w-[120px]">
                {r.last_seen_path ? <Globe className="w-3 h-3 flex-shrink-0" /> : null}
                <span className="truncate font-mono text-[10px]">{r.last_seen_path || '—'}</span>
              </div>
            </Td>
            <Td>
              <div className="flex flex-col">
                <span className="font-medium">{fmtLastSeen(r.last_seen_at, isID)}</span>
                <span className="text-[10px] t-text-muted">{fmtDateTime(r.last_seen_at, isID)}</span>
              </div>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest t-text-muted whitespace-nowrap">{children}</th>;
}
function Td({ children }) {
  return <td className="px-3 py-2 align-top">{children}</td>;
}
